import { VendorPickerBC } from '@/components/filtros/VendorPickerBC';
import { OrdenCompraDetalle } from '@/components/solicitudes-contado/OrdenCompraDetalle';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useAccionesSolicitudContado,
  useOrdenesCompraBC,
  useOrdenesPorProveedor,
} from '@/hooks/useSolicitudesContado';
import { useVencimientosDetalle } from '@/hooks/useVencimientosDetalle';
import { hoyCR } from '@/lib/fechaHora';
import { cn } from '@/lib/utils';
import {
  type TipoDocumentoSolicitud,
  formatearCRC,
  formatearUSD,
  normalizarNumeroOC,
} from '@adelante/shared';
import { CaretDown, CaretUp, CircleNotch, X } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

const TIPOS: { valor: TipoDocumentoSolicitud; texto: string; ayuda: string }[] = [
  {
    valor: 'ORDEN',
    texto: 'Orden de compra',
    ayuda: 'Pago de una OC de Business Central (total o parcial)',
  },
  {
    valor: 'FACTURA',
    texto: 'Factura',
    ayuda: 'Una factura ya registrada y abierta en Business Central',
  },
  {
    valor: 'MIXTO',
    texto: 'OC + factura',
    ayuda: 'Órdenes de compra y facturas del mismo proveedor en una sola solicitud',
  },
  {
    valor: 'OTRO',
    texto: 'Otro pago',
    ayuda: 'Sin documento: servicios, reparaciones, anticipos…',
  },
];

const monto = (m: number, moneda: string | null) =>
  moneda && moneda !== 'CRC' ? formatearUSD(m) : formatearCRC(m);

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Alta de una solicitud de pago de contado: reemplaza agregar una fila a la hoja semanal. */
export function NuevaSolicitudDialog({ open, onClose }: Props) {
  const { crear } = useAccionesSolicitudContado();
  const { data: vencData } = useVencimientosDetalle();
  const { data: ordenesPorProveedor } = useOrdenesPorProveedor(open);

  const [tipo, setTipo] = useState<TipoDocumentoSolicitud>('ORDEN');
  const [vendorCode, setVendorCode] = useState<string | null>(null);
  const [sinProveedorBC, setSinProveedorBC] = useState(false);
  const [beneficiario, setBeneficiario] = useState('');
  const [ordenesElegidas, setOrdenesElegidas] = useState<string[]>([]);
  const [detalleAbierto, setDetalleAbierto] = useState<string | null>(null);
  const [numeroManual, setNumeroManual] = useState('');
  const [entryNos, setEntryNos] = useState<number[]>([]);
  const [moneda, setMoneda] = useState<'CRC' | 'USD'>('CRC');
  const [montoTexto, setMontoTexto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fechaSolicitud, setFechaSolicitud] = useState(hoyCR());

  const {
    data: ordenesData,
    isLoading: cargandoOrdenes,
    isError: errorOrdenes,
  } = useOrdenesCompraBC(tipo === 'ORDEN' || tipo === 'MIXTO' ? vendorCode : null);
  const ordenes = ordenesData?.data ?? [];

  const facturasProveedor = useMemo(
    () =>
      (vencData?.data ?? []).filter(
        (f) => f.vendorCode === vendorCode && !f.pactado && (f.remainingAmount ?? 0) > 0,
      ),
    [vencData, vendorCode],
  );

  // Facturas abiertas y no pactadas, por proveedor: sirven para ofrecer primero a quien tiene algo pendiente.
  const facturasPorProveedor = useMemo(() => {
    const cuenta: Record<string, number> = {};
    for (const f of vencData?.data ?? []) {
      if (f.pactado || (f.remainingAmount ?? 0) <= 0) continue;
      cuenta[f.vendorCode] = (cuenta[f.vendorCode] ?? 0) + 1;
    }
    return cuenta;
  }, [vencData]);

  function reiniciar() {
    setTipo('ORDEN');
    setVendorCode(null);
    setSinProveedorBC(false);
    setBeneficiario('');
    setOrdenesElegidas([]);
    setDetalleAbierto(null);
    setNumeroManual('');
    setEntryNos([]);
    setMoneda('CRC');
    setMontoTexto('');
    setReferencia('');
    setDescripcion('');
    setFechaSolicitud(hoyCR());
  }

  function cambiarProveedor(codigo: string | null) {
    setVendorCode(codigo);
    setOrdenesElegidas([]);
    setDetalleAbierto(null);
    setNumeroManual('');
    setEntryNos([]);
  }

  const ordenPorNumero = useMemo(() => new Map(ordenes.map((o) => [o.numero, o])), [ordenes]);
  const esDolaresOrden = (o: { currencyCode: string | null }) =>
    !!o.currencyCode && o.currencyCode !== 'CRC';
  // La moneda la fija la primera orden elegida que BC conoce: no se mezclan colones y dólares.
  const monedaSeleccion = ordenesElegidas.map((n) => ordenPorNumero.get(n)).find((o) => !!o);

  /** Aplica la selección y, si BC conoce todas las órdenes, pone la suma como monto (más las facturas, si es mixta). */
  function aplicarSeleccion(lista: string[]) {
    setOrdenesElegidas(lista);
    const conocidas = lista.map((n) => ordenPorNumero.get(n));
    const primera = conocidas.find((o) => !!o);
    if (lista.length > 0 && conocidas.every((o) => !!o)) {
      const totalOC = conocidas.reduce((acc, o) => acc + (o?.monto ?? 0), 0);
      const totalFacturas =
        tipo === 'MIXTO'
          ? facturasProveedor
              .filter((f) => entryNos.includes(f.entryNo))
              .reduce((acc, f) => acc + (f.remainingAmount ?? 0), 0)
          : 0;
      setMontoTexto(String(Math.round((totalOC + totalFacturas) * 100) / 100));
    }
    if (primera) setMoneda(esDolaresOrden(primera) ? 'USD' : 'CRC');
  }

  function alternarOrden(numero: string) {
    aplicarSeleccion(
      ordenesElegidas.includes(numero)
        ? ordenesElegidas.filter((n) => n !== numero)
        : [...ordenesElegidas, numero],
    );
  }

  function agregarManual() {
    const numero = normalizarNumeroOC(numeroManual);
    if (!numero) return;
    if (!ordenesElegidas.includes(numero)) aplicarSeleccion([...ordenesElegidas, numero]);
    setNumeroManual('');
  }

  const esDolaresFactura = (f: { currencyCode: string | null }) =>
    !!f.currencyCode && f.currencyCode !== 'CRC';
  const facturasElegidas = facturasProveedor.filter((f) => entryNos.includes(f.entryNo));

  /** Aplica la selección de facturas y pone la suma de sus saldos como monto. */
  function aplicarFacturas(lista: number[]) {
    setEntryNos(lista);
    const elegidas = facturasProveedor.filter((f) => lista.includes(f.entryNo));
    const totalFacturas = elegidas.reduce((acc, f) => acc + (f.remainingAmount ?? 0), 0);
    // En una solicitud mixta el monto suma también las OC elegidas (si BC conoce todas sus montos).
    const ocConocidas = ordenesElegidas.map((n) => ordenPorNumero.get(n));
    const sumaOC =
      tipo === 'MIXTO' && ocConocidas.every((o) => !!o)
        ? ocConocidas.reduce((acc, o) => acc + (o?.monto ?? 0), 0)
        : 0;
    const total = totalFacturas + sumaOC;
    if (elegidas.length > 0) {
      setMontoTexto(String(Math.round(total * 100) / 100));
      const primera = elegidas[0];
      if (primera) setMoneda(esDolaresFactura(primera) ? 'USD' : 'CRC');
      if (!descripcion || descripcion.startsWith('Pago de factura'))
        setDescripcion(
          `Pago de factura${elegidas.length > 1 ? 's' : ''} ${elegidas.map((f) => f.documentNo ?? f.entryNo).join(', ')}`,
        );
    }
  }

  function alternarFactura(entryNo: number) {
    aplicarFacturas(
      entryNos.includes(entryNo) ? entryNos.filter((n) => n !== entryNo) : [...entryNos, entryNo],
    );
  }

  const montoNumero = Number(montoTexto.replace(',', '.'));
  const monedaBloqueada =
    ((tipo === 'FACTURA' || tipo === 'MIXTO') && entryNos.length > 0) ||
    ((tipo === 'ORDEN' || tipo === 'MIXTO') && !!monedaSeleccion);
  // Mixta: las OC y las facturas elegidas deben ser de la misma moneda.
  const mezclaMonedas =
    tipo === 'MIXTO' &&
    !!monedaSeleccion &&
    facturasElegidas.length > 0 &&
    esDolaresOrden(monedaSeleccion) !==
      esDolaresFactura(facturasElegidas[0] ?? { currencyCode: null });
  const hayOrdenSinMonto = ordenesElegidas.some((n) => !ordenPorNumero.has(n));

  const falta: string | null = (() => {
    if (tipo !== 'OTRO' || !sinProveedorBC) {
      if (!vendorCode) return 'Elegí el proveedor';
    } else if (!beneficiario.trim()) return 'Escribí el nombre del beneficiario';
    if (tipo === 'ORDEN' && ordenesElegidas.length === 0)
      return 'Elegí una o más órdenes, o escribí su número';
    if (tipo === 'FACTURA' && entryNos.length === 0) return 'Elegí una o más facturas';
    if (tipo === 'MIXTO' && ordenesElegidas.length + entryNos.length === 0)
      return 'Elegí al menos una orden o una factura';
    if (mezclaMonedas) return 'No se pueden mezclar colones y dólares en una solicitud';
    if (!descripcion.trim()) return 'Escribí una descripción';
    if (!(montoNumero > 0)) return 'Indicá un monto mayor a cero';
    return null;
  })();

  function enviar() {
    if (falta) return;
    const porBC = tipo !== 'OTRO' || !sinProveedorBC;
    crear.mutate(
      {
        tipoDocumento: tipo,
        vendorCode: porBC ? vendorCode : null,
        beneficiario: porBC ? null : beneficiario.trim(),
        currencyCode: moneda === 'USD' ? 'USD' : null,
        documentosBC: tipo === 'ORDEN' || tipo === 'MIXTO' ? ordenesElegidas : undefined,
        entryNos: tipo === 'FACTURA' || tipo === 'MIXTO' ? entryNos : undefined,
        referencia: referencia.trim() || null,
        descripcion: descripcion.trim(),
        montoSolicitado: montoNumero,
        fecha: fechaSolicitud || undefined,
      },
      {
        onSuccess: (r) => {
          toast.success('Solicitud registrada');
          if (r.data?.aviso) toast.warning(r.data.aviso);
          reiniciar();
          onClose();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo registrar'),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva solicitud de pago de contado</DialogTitle>
          <DialogDescription>
            Queda pendiente hasta que el administrador la apruebe. Lo no aprobado se arrastra a la
            semana siguiente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <fieldset
            className="m-0 grid grid-cols-2 gap-2 border-0 p-0 sm:grid-cols-4"
            aria-label="Tipo de pago"
          >
            {TIPOS.map((t) => (
              <button
                key={t.valor}
                type="button"
                aria-pressed={tipo === t.valor}
                title={t.ayuda}
                onClick={() => {
                  setTipo(t.valor);
                  setOrdenesElegidas([]);
                  setDetalleAbierto(null);
                  setNumeroManual('');
                  setEntryNos([]);
                }}
                className={cn(
                  'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                  tipo === t.valor
                    ? 'border-adelante-primary bg-adelante-primary/15'
                    : 'border-border hover:bg-muted',
                )}
              >
                {t.texto}
              </button>
            ))}
          </fieldset>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{tipo === 'OTRO' && sinProveedorBC ? 'Beneficiario' : 'Proveedor'}</Label>
              {tipo === 'OTRO' && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={sinProveedorBC}
                    onChange={(e) => {
                      setSinProveedorBC(e.target.checked);
                      cambiarProveedor(null);
                    }}
                  />
                  No es un proveedor de BC
                </label>
              )}
            </div>
            {tipo === 'OTRO' && sinProveedorBC ? (
              <Input
                value={beneficiario}
                onChange={(e) => setBeneficiario(e.target.value)}
                placeholder="Nombre de quien recibe el pago"
                maxLength={200}
              />
            ) : (
              <VendorPickerBC
                value={vendorCode}
                onChange={cambiarProveedor}
                placeholder="Elegir proveedor de BC"
                enLinea
                filtroContado
                documentosPorProveedor={
                  tipo === 'ORDEN' || tipo === 'MIXTO'
                    ? ordenesPorProveedor
                    : tipo === 'FACTURA'
                      ? facturasPorProveedor
                      : undefined
                }
                etiquetaDocumentos={tipo === 'FACTURA' ? 'fact.' : 'OC'}
                soloConDocumentos={tipo === 'FACTURA'}
              />
            )}
          </div>

          {(tipo === 'ORDEN' || tipo === 'MIXTO') && vendorCode && (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label>Órdenes de compra</Label>
                <span className="text-xs text-muted-foreground">
                  Marcá una o varias; el monto es la suma
                </span>
              </div>
              {cargandoOrdenes ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
                  Leyendo órdenes de Business Central…
                </p>
              ) : errorOrdenes ? (
                <p className="text-sm text-amber-700">
                  No se pudieron leer las órdenes de BC. Podés escribir los números abajo.
                </p>
              ) : ordenes.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Este proveedor no tiene órdenes de compra abiertas en BC. Si es una anterior,
                  escribí el número abajo.
                </p>
              ) : (
                <div className="max-h-64 space-y-0.5 overflow-y-auto rounded-md border p-1">
                  {ordenes.map((o) => {
                    const marcada = ordenesElegidas.includes(o.numero);
                    const otraMoneda =
                      !!monedaSeleccion &&
                      !marcada &&
                      esDolaresOrden(o) !== esDolaresOrden(monedaSeleccion);
                    return (
                      <div key={o.numero} className="rounded">
                        <div
                          className={cn(
                            'flex items-center gap-2 px-2 py-1.5 text-sm',
                            marcada ? 'bg-adelante-primary/15' : 'hover:bg-muted',
                            otraMoneda && 'opacity-50',
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={marcada}
                            disabled={otraMoneda}
                            onChange={() => alternarOrden(o.numero)}
                            aria-label={`Incluir ${o.numero}`}
                            title={
                              otraMoneda ? 'Está en otra moneda que las ya elegidas' : undefined
                            }
                          />
                          <button
                            type="button"
                            disabled={otraMoneda}
                            onClick={() => alternarOrden(o.numero)}
                            className="flex flex-1 items-center justify-between gap-3 text-left"
                          >
                            <span className="font-medium">{o.numero}</span>
                            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                              {o.fecha ?? '—'} · {o.estado}
                              {o.obraCodigo ? ` · ${o.obraCodigo}` : ''}
                            </span>
                            <span className="tabular-nums">{monto(o.monto, o.currencyCode)}</span>
                          </button>
                          <button
                            type="button"
                            title="Ver el detalle de la orden: obra y líneas"
                            aria-expanded={detalleAbierto === o.numero}
                            onClick={() =>
                              setDetalleAbierto(detalleAbierto === o.numero ? null : o.numero)
                            }
                            className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            Detalle
                            {detalleAbierto === o.numero ? (
                              <CaretUp className="h-3 w-3" weight="bold" aria-hidden="true" />
                            ) : (
                              <CaretDown className="h-3 w-3" weight="bold" aria-hidden="true" />
                            )}
                          </button>
                        </div>
                        {detalleAbierto === o.numero && (
                          <div className="border-t bg-muted/30 px-3 py-2">
                            <OrdenCompraDetalle numero={o.numero} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={numeroManual}
                  onChange={(e) => setNumeroManual(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      agregarManual();
                    }
                  }}
                  placeholder="O escribí el N.º de OC y agregalo (ej. OC5476 o 5476)"
                  maxLength={50}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!numeroManual.trim()}
                  onClick={agregarManual}
                >
                  Agregar
                </Button>
              </div>
              {ordenesElegidas.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Elegidas:</span>
                  {ordenesElegidas.map((n) => (
                    <span
                      key={n}
                      className="inline-flex items-center gap-1 rounded-full bg-adelante-primary/15 px-2 py-0.5 text-xs font-medium"
                    >
                      {n}
                      <button
                        type="button"
                        aria-label={`Quitar ${n}`}
                        onClick={() => aplicarSeleccion(ordenesElegidas.filter((x) => x !== n))}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" weight="bold" aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {hayOrdenSinMonto && (
                <p className="text-xs text-amber-700">
                  Alguna orden no está entre las abiertas de BC: escribí el monto a mano.
                </p>
              )}
            </div>
          )}

          {(tipo === 'FACTURA' || tipo === 'MIXTO') && vendorCode && (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label>Facturas abiertas</Label>
                <span className="text-xs text-muted-foreground">
                  Marcá una o varias; el monto es la suma
                </span>
              </div>
              {facturasProveedor.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Este proveedor no tiene facturas abiertas en BC.
                </p>
              ) : (
                <div className="max-h-64 space-y-0.5 overflow-y-auto rounded-md border p-1">
                  {facturasProveedor.map((f) => {
                    const marcada = entryNos.includes(f.entryNo);
                    const primera = facturasElegidas[0];
                    const otraMoneda =
                      !!primera && !marcada && esDolaresFactura(f) !== esDolaresFactura(primera);
                    return (
                      <label
                        key={f.entryNo}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm',
                          marcada ? 'bg-adelante-primary/15' : 'hover:bg-muted',
                          otraMoneda && 'cursor-not-allowed opacity-50',
                        )}
                        title={otraMoneda ? 'Está en otra moneda que las ya elegidas' : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={marcada}
                          disabled={otraMoneda}
                          onChange={() => alternarFactura(f.entryNo)}
                          aria-label={`Incluir ${f.documentNo ?? f.entryNo}`}
                        />
                        <span className="w-28 font-medium">{f.documentNo ?? f.entryNo}</span>
                        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                          vence {f.dueDate ?? '—'}
                          {f.externalDocumentNo ? ` · ${f.externalDocumentNo}` : ''}
                        </span>
                        <span className="tabular-nums">
                          {monto(f.remainingAmount ?? 0, f.currencyCode)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {facturasElegidas.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {facturasElegidas.length} factura(s) marcada(s)
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-[1fr_auto] items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="monto-solicitud">Monto solicitado</Label>
              <div className="flex gap-2">
                <Input
                  id="monto-solicitud"
                  inputMode="decimal"
                  value={montoTexto}
                  onChange={(e) => setMontoTexto(e.target.value)}
                  placeholder="0.00"
                />
                {((tipo === 'ORDEN' || tipo === 'MIXTO') && ordenesElegidas.length > 0) ||
                ((tipo === 'FACTURA' || tipo === 'MIXTO') && entryNos.length > 0) ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    title="Poner la mitad del total (anticipo del 50%)"
                    onClick={() =>
                      montoNumero > 0 && setMontoTexto(String(Math.round(montoNumero * 50) / 100))
                    }
                  >
                    50%
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="moneda-solicitud">Moneda</Label>
              <select
                id="moneda-solicitud"
                value={moneda}
                disabled={monedaBloqueada}
                onChange={(e) => setMoneda(e.target.value as 'CRC' | 'USD')}
                className="h-9 rounded-md border border-input bg-transparent px-2 text-sm disabled:opacity-60"
              >
                <option value="CRC">Colones (₡)</option>
                <option value="USD">Dólares ($)</option>
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="fecha-solicitud">Fecha en que Proveeduría la solicitó</Label>
            <Input
              id="fecha-solicitud"
              type="date"
              value={fechaSolicitud}
              max={hoyCR()}
              onChange={(e) => setFechaSolicitud(e.target.value)}
              className="w-44"
            />
            <p className="text-xs text-muted-foreground">
              Poné la fecha del correo o WhatsApp si no la están cargando el mismo día.
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="desc-solicitud">Descripción</Label>
            <Input
              id="desc-solicitud"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Qué se paga (ej. Trimoto para Cañas)"
              maxLength={300}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ref-solicitud">Referencia (opcional)</Label>
            <Input
              id="ref-solicitud"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Ej. varias OC: OC5480-5585"
              maxLength={100}
            />
          </div>
        </div>

        <DialogFooter className="items-center gap-2">
          {falta && <span className="mr-auto text-xs text-muted-foreground">{falta}</span>}
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={!!falta || crear.isPending} onClick={enviar}>
            {crear.isPending ? 'Guardando…' : 'Registrar solicitud'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
